import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Send, User, Award, Building2,
  CheckCircle, XCircle, AlertCircle, Search, ChevronDown,
  Clock, FileX, Calendar, X
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import api from '../../services/apiService';
import SuspendedCompanyOverlay from './shared/SuspendedCompanyOverlay';

const WindCompanyNominationCreate = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLevel = searchParams.get('level');

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    authorization_levels: preselectedLevel ? [parseInt(preselectedLevel)] : [],
    user: '',
    date_start: '',
    date_end: '',
    comment: ''
  });

  const [authorizationLevels, setAuthorizationLevels] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [technicianSearch, setTechnicianSearch] = useState('');
  const [showTechnicianDropdown, setShowTechnicianDropdown] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
        const [levelsResponse, usersResponse, qualTypesResponse] = await Promise.all([
          api.get('/oem/authorization-levels/'),
          api.get('/users/', { params: { fields: 'full' } }),
          api.get('/qualificationtype/')
        ]);

        setAuthorizationLevels(levelsResponse.data);
        setQualificationTypes(qualTypesResponse.data || []);

        if (preselectedLevel) {
          const level = levelsResponse.data.find(l => l.id === parseInt(preselectedLevel));
          if (level) {
            setSelectedLevels([level]);
          }
        }

        // Filter users for current company
        const companyTechnicians = usersResponse.data.filter(u =>
          u.company === user?.company || u.company_name === user?.company
        );
        setCompanyUsers(companyTechnicians);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        if (err.response?.status === 403) {
          setError('Your company does not have access to any OEM authorization levels.');
        } else {
          setError('Failed to load data');
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [user?.company, preselectedLevel]);

  const filteredTechnicians = companyUsers.filter(tech => {
    if (!technicianSearch) return true;
    const searchLower = technicianSearch.toLowerCase();
    const fullName = `${tech.first_name || ''} ${tech.last_name || ''}`.toLowerCase();
    return fullName.includes(searchLower) ||
           tech.email?.toLowerCase().includes(searchLower) ||
           tech.username?.toLowerCase().includes(searchLower);
  });

  const levelsByOemCompany = authorizationLevels.reduce((acc, level) => {
    const companyName = level.oem_company_name || 'Unknown Company';
    if (!acc[companyName]) {
      acc[companyName] = [];
    }
    acc[companyName].push(level);
    return acc;
  }, {});

  const handleLevelToggle = (levelId) => {
    const id = parseInt(levelId);
    const level = authorizationLevels.find(l => l.id === id);

    setSelectedLevels(prev => {
      const isSelected = prev.some(l => l.id === id);
      if (isSelected) {
        return prev.filter(l => l.id !== id);
      } else {
        return [...prev, level];
      }
    });

    setFormData(prev => {
      const isSelected = prev.authorization_levels.includes(id);
      return {
        ...prev,
        authorization_levels: isSelected
          ? prev.authorization_levels.filter(lid => lid !== id)
          : [...prev.authorization_levels, id]
      };
    });
  };

  const handleTechnicianSelect = (tech) => {
    setSelectedTechnician(tech);
    setFormData(prev => ({
      ...prev,
      user: tech.id
    }));
    setTechnicianSearch(`${tech.first_name || ''} ${tech.last_name || ''}`.trim() || tech.username);
    setShowTechnicianDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (formData.authorization_levels.length === 0 || !formData.user) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (!formData.date_start || !formData.date_end) {
      setError('Please select validity dates');
      setLoading(false);
      return;
    }

    if (new Date(formData.date_end) < new Date(formData.date_start)) {
      setError('End date must be after start date');
      setLoading(false);
      return;
    }

    // Check document requirements (only required documents, not alternative groups)
    const reqCheck = getDetailedRequirements();
    if (reqCheck && !reqCheck.meetsRequirements) {
      const issues = [];
      if (reqCheck.expiredCount > 0) issues.push(`${reqCheck.expiredCount} expired document(s)`);
      if (reqCheck.missingCount > 0) issues.push(`${reqCheck.missingCount} missing document(s)`);
      setError(`Cannot submit nomination: ${issues.join(', ')}. All required documents must be valid.`);
      setLoading(false);
      return;
    }

    try {
      // Create a nomination for each selected level
      const nominations = await Promise.all(
        formData.authorization_levels.map(async (levelId) => {
          const nominationData = {
            authorization_level: levelId,
            user: formData.user,
            date_start: formData.date_start,
            date_end: formData.date_end,
            comment: formData.comment
          };
          const response = await api.post('/oem/nominations/', nominationData);

          // Auto-submit each nomination
          try {
            await api.post(`/oem/nominations/${response.data.id}/submit/`);
          } catch (submitErr) {
            console.log('Could not auto-submit nomination:', submitErr);
          }

          return response.data;
        })
      );

      setSuccess(true);
      setTimeout(() => {
        navigate('/oem-company/nominations');
      }, 1500);
    } catch (err) {
      console.error('Error creating nomination:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to create nomination');
    } finally {
      setLoading(false);
    }
  };

  const getUserQualification = (docId) => {
    if (!selectedTechnician?.qualifications) return null;
    return selectedTechnician.qualifications.find(q => {
      const qualTypeId = typeof q.qualification_type === 'object'
        ? q.qualification_type?.id
        : q.qualification_type;
      return qualTypeId === docId;
    });
  };

  const isQualificationExpired = (qual) => {
    if (!qual?.expiry_date) return false;
    return new Date(qual.expiry_date) < new Date();
  };

  // Check if a technician meets requirements for selected levels
  const checkTechnicianMeetsRequirements = (tech) => {
    if (selectedLevels.length === 0) return { meets: true, hasRequirements: false };

    // Combine required documents from all selected levels
    const allRequiredDocIds = new Set();
    selectedLevels.forEach(level => {
      (level.required_documents || []).forEach(docId => allRequiredDocIds.add(docId));
      (level.required_documents_data || []).forEach(doc => allRequiredDocIds.add(doc.id));
    });

    if (allRequiredDocIds.size === 0) return { meets: true, hasRequirements: false };

    const techQuals = tech.qualifications || [];
    const today = new Date();
    let validCount = 0;
    let expiredCount = 0;
    let missingCount = 0;

    allRequiredDocIds.forEach(docId => {
      const qual = techQuals.find(q => {
        const qualTypeId = typeof q.qualification_type === 'object'
          ? q.qualification_type?.id
          : q.qualification_type;
        return qualTypeId === docId;
      });

      if (!qual) {
        missingCount++;
      } else if (qual.expiry_date && new Date(qual.expiry_date) < today) {
        expiredCount++;
      } else {
        validCount++;
      }
    });

    return {
      meets: expiredCount === 0 && missingCount === 0,
      hasRequirements: true,
      validCount,
      expiredCount,
      missingCount,
      totalRequired: allRequiredDocIds.size
    };
  };

  const clearSelectedTechnician = () => {
    setSelectedTechnician(null);
    setFormData(prev => ({ ...prev, user: '' }));
    setTechnicianSearch('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDetailedRequirements = () => {
    if (!selectedTechnician || selectedLevels.length === 0) return null;

    // Combine required documents from all selected levels
    const allRequiredDocs = [];
    const allRequiredDocIds = [];
    const seenDocIds = new Set();

    // Collect all alternative groups from selected levels
    const allAlternativeGroups = [];

    selectedLevels.forEach(level => {
      (level.required_documents_data || []).forEach(doc => {
        if (!seenDocIds.has(doc.id)) {
          seenDocIds.add(doc.id);
          allRequiredDocs.push(doc);
        }
      });
      (level.required_documents || []).forEach(docId => {
        if (!allRequiredDocIds.includes(docId)) {
          allRequiredDocIds.push(docId);
        }
      });
      // Collect alternative groups
      (level.required_doc_groups || []).forEach(group => {
        if (group && group.length > 0) {
          allAlternativeGroups.push({
            levelCode: level.code,
            levelName: level.name,
            docIds: group
          });
        }
      });
    });

    const requiredDocs = allRequiredDocs;
    const requiredDocIds = allRequiredDocIds;

    const documentStatus = requiredDocs.map(doc => {
      const userQual = getUserQualification(doc.id);
      const hasDocument = !!userQual;
      const isExpired = hasDocument && isQualificationExpired(userQual);
      const isValid = hasDocument && !isExpired;

      return {
        id: doc.id,
        name: doc.name,
        category: doc.category,
        hasDocument,
        isExpired,
        isValid,
        expiryDate: userQual?.expiry_date,
        certificateNumber: userQual?.certificate_number
      };
    });

    const docDataIds = requiredDocs.map(d => d.id);
    const missingFromData = requiredDocIds.filter(id => !docDataIds.includes(id));

    missingFromData.forEach(docId => {
      const userQual = getUserQualification(docId);
      const hasDocument = !!userQual;
      const isExpired = hasDocument && isQualificationExpired(userQual);
      const isValid = hasDocument && !isExpired;

      documentStatus.push({
        id: docId,
        name: `Document ID: ${docId}`,
        category: null,
        hasDocument,
        isExpired,
        isValid,
        expiryDate: userQual?.expiry_date,
        certificateNumber: userQual?.certificate_number
      });
    });

    // Helper function to get document name from qualificationTypes
    const getDocumentName = (docId) => {
      // First check in required_documents_data
      const fromRequired = allRequiredDocs.find(d => d.id === docId);
      if (fromRequired) return fromRequired.name;
      // Then check in qualification types
      const qualType = qualificationTypes.find(q => q.id === docId);
      if (qualType) return qualType.name;
      return `Document ID: ${docId}`;
    };

    // Process alternative groups
    const alternativeGroupsStatus = allAlternativeGroups.map((group, index) => {
      const docsInGroup = group.docIds.map(docId => {
        const userQual = getUserQualification(docId);
        const hasDocument = !!userQual;
        const isExpired = hasDocument && isQualificationExpired(userQual);
        const isValid = hasDocument && !isExpired;

        return {
          id: docId,
          name: getDocumentName(docId),
          hasDocument,
          isExpired,
          isValid,
          expiryDate: userQual?.expiry_date
        };
      });

      // Alternative group is satisfied if at least ONE document is valid
      const hasValidDoc = docsInGroup.some(d => d.isValid);

      return {
        index: index + 1,
        levelCode: group.levelCode,
        docs: docsInGroup,
        isSatisfied: hasValidDoc
      };
    });

    const validCount = documentStatus.filter(d => d.isValid).length;
    const expiredCount = documentStatus.filter(d => d.isExpired).length;
    const missingCount = documentStatus.filter(d => !d.hasDocument).length;
    const totalRequired = documentStatus.length;

    // Check if all alternative groups are satisfied
    const allAlternativeGroupsSatisfied = alternativeGroupsStatus.every(g => g.isSatisfied);
    const unsatisfiedAlternativeGroups = alternativeGroupsStatus.filter(g => !g.isSatisfied);

    return {
      documents: documentStatus,
      alternativeGroups: alternativeGroupsStatus,
      validCount,
      expiredCount,
      missingCount,
      totalRequired,
      unsatisfiedAlternativeGroupsCount: unsatisfiedAlternativeGroups.length,
      // Only required documents matter - alternative groups don't block nomination
      meetsRequirements: validCount === totalRequired
    };
  };

  const requirementCheck = getDetailedRequirements();

  // Avatar component
  const UserAvatar = ({ userData, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8 text-xs',
      md: 'w-10 h-10 text-sm',
      lg: 'w-12 h-12 text-base'
    };

    if (userData?.profile_picture) {
      return (
        <img
          src={userData.profile_picture}
          alt={`${userData.first_name} ${userData.last_name}`}
          className={`${sizeClasses[size]} rounded-xl object-cover border-2 border-orange-100`}
        />
      );
    }

    return (
      <div className={`${sizeClasses[size]} rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black`}>
        {userData?.first_name?.charAt(0)?.toUpperCase() || userData?.username?.charAt(0)?.toUpperCase() || 'U'}
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (authorizationLevels.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-5">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/oem-company')}
                className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-slate-600" />
              </button>
              <div>
                <h1 className="text-base font-black text-slate-900 uppercase tracking-tight">Create Nomination</h1>
                <p className="text-[10px] text-slate-500 font-medium">Submit authorization request</p>
              </div>
            </div>
          </div>
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Award className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-tight">No Authorization Levels</h3>
            <p className="text-xs text-slate-500">
              Contact your OEM Master administrator to be added to a project.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SuspendedCompanyOverlay>
      <div className="p-6 max-w-6xl mx-auto space-y-5">
        {/* Header Card */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/oem-company/nominations')}
                className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-slate-600" />
              </button>
              <div>
                <h1 className="text-base font-black text-slate-900 uppercase tracking-tight">Create Nomination</h1>
                <p className="text-[10px] text-slate-500 font-medium">Submit authorization request</p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-black">Nomination created successfully! Redirecting...</span>
          </div>
        )}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
            <XCircle className="h-5 w-5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-12 gap-5">
            {/* Left Column - Selection */}
            <div className="col-span-5 space-y-5">
              {/* Authorization Levels */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                      <Award className="h-4 w-4 text-violet-600" />
                    </div>
                    <h2 className="text-[10px] font-black text-slate-900 uppercase tracking-wide">Authorization Levels</h2>
                  </div>
                  {selectedLevels.length > 0 && (
                    <span className="px-2 py-0.5 text-[9px] font-black rounded-md bg-violet-100 text-violet-600 uppercase">
                      {selectedLevels.length} selected
                    </span>
                  )}
                </div>
                <div className="p-4 max-h-64 overflow-y-auto">
                  {Object.entries(levelsByOemCompany).map(([companyName, levels]) => (
                    <div key={companyName} className="mb-4 last:mb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wide">{companyName}</span>
                      </div>
                      <div className="space-y-2">
                        {levels.map((level) => {
                          const isSelected = formData.authorization_levels.includes(level.id);
                          return (
                            <label
                              key={level.id}
                              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border-2 transition-all ${
                                isSelected
                                  ? 'bg-orange-50 border-orange-200'
                                  : 'bg-slate-50 border-transparent hover:bg-slate-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleLevelToggle(level.id)}
                                className="w-4 h-4 rounded text-orange-500 border-slate-300 focus:ring-orange-500"
                              />
                              <span
                                className="px-2 py-0.5 rounded-md text-[9px] font-black text-white uppercase"
                                style={{ backgroundColor: level.color || '#F97316' }}
                              >
                                {level.code}
                              </span>
                              <span className="text-xs font-black text-slate-700">{level.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technician Selection */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                    <h2 className="text-[10px] font-black text-slate-900 uppercase tracking-wide">Select Technician</h2>
                  </div>
                  {selectedLevels.length > 0 && (
                    <span className="flex items-center gap-1 text-[9px] text-emerald-600 font-black uppercase">
                      <CheckCircle className="h-3 w-3" />
                      = ready
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={technicianSearch}
                      onChange={(e) => {
                        setTechnicianSearch(e.target.value);
                        setShowTechnicianDropdown(true);
                        if (selectedTechnician) {
                          setSelectedTechnician(null);
                          setFormData(prev => ({ ...prev, user: '' }));
                        }
                      }}
                      onFocus={() => setShowTechnicianDropdown(true)}
                      placeholder="Search by name..."
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`w-full h-11 pl-11 rounded-xl border bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all cursor-pointer text-sm ${
                        selectedTechnician ? 'pr-20 border-emerald-300 bg-emerald-50' : 'pr-10 border-slate-200'
                      }`}
                    />
                    {selectedTechnician ? (
                      <button
                        type="button"
                        onClick={clearSelectedTechnician}
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-7 px-2 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-lg flex items-center gap-1 text-[10px] font-black uppercase transition-colors"
                      >
                        <X className="h-3 w-3" />
                        Clear
                      </button>
                    ) : (
                      <ChevronDown className={`absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none transition-transform ${showTechnicianDropdown ? 'rotate-180' : ''}`} />
                    )}

                    {showTechnicianDropdown && !selectedTechnician && (
                      <div className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
                        {filteredTechnicians.length === 0 ? (
                          <div className="px-4 py-8 text-center text-slate-500 text-xs">No technicians found</div>
                        ) : (
                          <div className="py-2">
                            {/* Sort technicians - those meeting requirements first */}
                            {filteredTechnicians
                              .map(tech => ({ ...tech, reqCheck: checkTechnicianMeetsRequirements(tech) }))
                              .sort((a, b) => {
                                if (a.reqCheck.meets && !b.reqCheck.meets) return -1;
                                if (!a.reqCheck.meets && b.reqCheck.meets) return 1;
                                return 0;
                              })
                              .map((tech) => {
                                const reqCheck = tech.reqCheck;
                                return (
                                  <div
                                    key={tech.id}
                                    onClick={() => handleTechnicianSelect(tech)}
                                    className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors ${
                                      reqCheck.meets && reqCheck.hasRequirements
                                        ? 'bg-emerald-50 hover:bg-emerald-100 border-l-4 border-emerald-500'
                                        : 'hover:bg-slate-50'
                                    }`}
                                  >
                                    <UserAvatar userData={tech} size="sm" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-black text-slate-900 flex items-center gap-2">
                                        {tech.first_name || ''} {tech.last_name || ''}
                                        {!tech.first_name && !tech.last_name && tech.username}
                                        {reqCheck.meets && reqCheck.hasRequirements && (
                                          <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                        )}
                                      </div>
                                      <div className="text-[10px] text-slate-500 font-medium">{tech.email}</div>
                                    </div>
                                    {reqCheck.hasRequirements && (
                                      <div className="flex-shrink-0 text-right">
                                        {reqCheck.meets ? (
                                          <span className="text-[9px] font-black text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-md uppercase">
                                            {reqCheck.validCount}/{reqCheck.totalRequired} valid
                                          </span>
                                        ) : (
                                          <span className="text-[9px] font-black text-red-600 bg-red-100 px-2 py-0.5 rounded-md uppercase">
                                            {reqCheck.expiredCount > 0 && `${reqCheck.expiredCount} exp`}
                                            {reqCheck.expiredCount > 0 && reqCheck.missingCount > 0 && ', '}
                                            {reqCheck.missingCount > 0 && `${reqCheck.missingCount} miss`}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="col-span-7">
              {!selectedTechnician || selectedLevels.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm h-full flex items-center justify-center">
                  <div className="text-center py-16">
                    <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <User className="h-8 w-8 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-black text-slate-900 mb-2 uppercase tracking-tight">Configure Nomination</h3>
                    <p className="text-xs text-slate-500">Select authorization level and technician to continue</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  {/* Technician Header */}
                  <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <UserAvatar userData={selectedTechnician} size="lg" />
                        <div>
                          <h3 className="text-sm font-black text-slate-900">
                            {selectedTechnician.first_name} {selectedTechnician.last_name}
                          </h3>
                          <p className="text-[10px] text-slate-500 font-medium">{selectedTechnician.email}</p>
                        </div>
                      </div>
                      {requirementCheck && (
                        <span className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase ${
                          requirementCheck.meetsRequirements
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-red-50 text-red-700'
                        }`}>
                          {requirementCheck.meetsRequirements
                            ? 'All documents valid'
                            : `${requirementCheck.expiredCount > 0 ? `${requirementCheck.expiredCount} expired` : ''}${requirementCheck.expiredCount > 0 && requirementCheck.missingCount > 0 ? ', ' : ''}${requirementCheck.missingCount > 0 ? `${requirementCheck.missingCount} missing` : ''}`}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-5 space-y-5">
                    {/* Documents */}
                    {requirementCheck && requirementCheck.documents.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-wide">
                            Required Documents for {selectedLevels.map(l => l.code).join(', ')}
                          </label>
                          <span className="text-[10px] text-slate-500 font-black">
                            {requirementCheck.validCount}/{requirementCheck.totalRequired} valid
                          </span>
                        </div>
                        <div className="space-y-2">
                          {requirementCheck.documents.map((doc) => (
                            <div
                              key={doc.id}
                              className={`px-4 py-3 rounded-xl flex items-center justify-between ${
                                doc.isValid
                                  ? 'bg-emerald-50 border border-emerald-200'
                                  : doc.isExpired
                                  ? 'bg-red-50 border border-red-200'
                                  : 'bg-amber-50 border border-amber-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {doc.isValid ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                                ) : doc.isExpired ? (
                                  <Clock className="h-4 w-4 text-red-600" />
                                ) : (
                                  <FileX className="h-4 w-4 text-amber-600" />
                                )}
                                <div>
                                  <span className="text-xs font-black text-slate-900">{doc.name}</span>
                                  {doc.category && (
                                    <span className="text-[10px] text-slate-500 ml-2">· {doc.category}</span>
                                  )}
                                </div>
                              </div>
                              <span className={`text-[10px] font-black uppercase ${
                                doc.isValid ? 'text-emerald-700' : doc.isExpired ? 'text-red-700' : 'text-amber-700'
                              }`}>
                                {doc.isValid ? (doc.expiryDate ? formatDate(doc.expiryDate) : 'Valid') : doc.isExpired ? 'Expired' : 'Missing'}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Alternative Groups */}
                        {requirementCheck.alternativeGroups && requirementCheck.alternativeGroups.length > 0 && (
                          <div className="mt-4">
                            <div className="flex items-center gap-2 mb-3">
                              <label className="text-[10px] font-black text-amber-600 uppercase tracking-wide">
                                Alternative Documents
                              </label>
                              <span className="text-[8px] font-medium text-amber-500 bg-amber-100 px-1.5 py-0.5 rounded">
                                ONE from each group required
                              </span>
                            </div>
                            <div className="space-y-3">
                              {requirementCheck.alternativeGroups.map((group) => (
                                <div
                                  key={`group-${group.index}`}
                                  className={`p-3 rounded-xl border ${
                                    group.isSatisfied
                                      ? 'bg-emerald-50 border-emerald-200'
                                      : 'bg-amber-50 border-amber-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                                        group.isSatisfied
                                          ? 'bg-emerald-200 text-emerald-800'
                                          : 'bg-amber-200 text-amber-800'
                                      }`}>
                                        OR Group {group.index}
                                      </span>
                                      <span className="text-[9px] text-slate-500">({group.levelCode})</span>
                                    </div>
                                    {group.isSatisfied ? (
                                      <span className="flex items-center gap-1 text-[9px] font-black text-emerald-600">
                                        <CheckCircle className="h-3 w-3" />
                                        Satisfied
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-[9px] font-black text-amber-600">
                                        <AlertCircle className="h-3 w-3" />
                                        Need 1 valid
                                      </span>
                                    )}
                                  </div>
                                  <div className="space-y-1.5">
                                    {group.docs.map((doc) => (
                                      <div
                                        key={doc.id}
                                        className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                                          doc.isValid
                                            ? 'bg-emerald-100'
                                            : doc.isExpired
                                            ? 'bg-red-100'
                                            : 'bg-white'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          {doc.isValid ? (
                                            <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                                          ) : doc.isExpired ? (
                                            <Clock className="h-3.5 w-3.5 text-red-500" />
                                          ) : (
                                            <FileX className="h-3.5 w-3.5 text-slate-400" />
                                          )}
                                          <span className="text-[11px] font-bold text-slate-700">{doc.name}</span>
                                        </div>
                                        <span className={`text-[9px] font-black uppercase ${
                                          doc.isValid ? 'text-emerald-600' : doc.isExpired ? 'text-red-500' : 'text-slate-400'
                                        }`}>
                                          {doc.isValid ? 'Valid' : doc.isExpired ? 'Expired' : 'Missing'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {!requirementCheck.meetsRequirements && (
                          <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-red-700">
                              <p className="font-black mb-1">Cannot submit nomination</p>
                              <p>
                                {requirementCheck.expiredCount > 0 && `${requirementCheck.expiredCount} document(s) expired`}
                                {requirementCheck.expiredCount > 0 && requirementCheck.missingCount > 0 && ', '}
                                {requirementCheck.missingCount > 0 && `${requirementCheck.missingCount} document(s) missing`}.
                                {' '}All required documents must be valid before submitting.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Validity Period */}
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-wide">Validity Period</label>
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <label className="text-[10px] text-slate-600 font-medium mb-1.5 block">Start Date *</label>
                          <div className="relative">
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                              type="date"
                              value={formData.date_start}
                              onChange={(e) => {
                                const startDate = e.target.value;
                                let endDate = '';
                                if (startDate) {
                                  const start = new Date(startDate);
                                  start.setFullYear(start.getFullYear() + 3);
                                  endDate = start.toISOString().split('T')[0];
                                }
                                setFormData(prev => ({ ...prev, date_start: startDate, date_end: endDate }));
                              }}
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full h-11 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-sm"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-600 font-medium mb-1.5 block">End Date *</label>
                          <div className="relative">
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                              type="date"
                              value={formData.date_end}
                              onChange={(e) => setFormData(prev => ({ ...prev, date_end: e.target.value }))}
                              min={formData.date_start || new Date().toISOString().split('T')[0]}
                              className="w-full h-11 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-sm"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Comments */}
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-wide">Additional Comments</label>
                      <textarea
                        value={formData.comment}
                        onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                        placeholder="Optional notes for reviewer..."
                        rows={3}
                        className="w-full mt-3 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all resize-none text-sm"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={() => navigate('/oem-company/nominations')}
                        className="h-10 px-5 rounded-xl border border-slate-200 text-slate-700 font-black text-[10px] uppercase tracking-wide hover:bg-slate-50 transition-colors"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="h-10 px-5 bg-orange-500 text-white rounded-xl font-black text-[10px] uppercase tracking-wide hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20"
                        disabled={loading || success || (requirementCheck && !requirementCheck.meetsRequirements)}
                        title={requirementCheck && !requirementCheck.meetsRequirements ? 'All required documents must be valid' : ''}
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            Submit Nomination
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>

        {showTechnicianDropdown && (
          <div className="fixed inset-0 z-10" onClick={() => setShowTechnicianDropdown(false)} />
        )}
      </div>
    </SuspendedCompanyOverlay>
  );
};

export default WindCompanyNominationCreate;
